import { useEffect, useState } from 'react';
import { Button, Divider, Group, Modal, Stack, Switch, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import type { CaminoView } from '@/types/camino';
import styles from './EditCaminoModal.module.sass';
import { SaveErrorBox } from '@/components/SaveErrorBox/SaveErrorBox';
import type { ErrorResponse } from '@/types/error';
import type { AxiosError } from 'axios';

type EditCaminoModalProps = {
  opened: boolean;
  caminoData: CaminoView | null;
  onClose: () => void;
  onSave: (data: CaminoView) => Promise<void> | void;
};

export const EditCaminoModal = ({ opened, caminoData, onClose, onSave }: EditCaminoModalProps) => {
  const [isActive, setIsActive] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [saveError, setSaveError] = useState<AxiosError<ErrorResponse> | null>(null);

  const isValidUrl = (value: string) => {
    try {
      const parsedUrl = new URL(value);
      return parsedUrl.protocol === 'http:' || parsedUrl.protocol === 'https:';
    } catch {
      return false;
    }
  };

  const form = useForm<{ parishId: string; dioceseId: string; url: string; comment: string }>({
    initialValues: {
      parishId: '',
      dioceseId: '',
      url: '',
      comment: '',
    },
    validate: {
      parishId: (value) => {
        if (!isActive) {
          return null;
        }

        if (!value.trim()) {
          return 'Parish ID is required';
        }

        return null;
      },
      dioceseId: (value) => {
        if (!isActive) {
          return null;
        }

        if (!value.trim()) {
          return 'Diocese ID is required';
        }

        return null;
      },
      url: (value) => {
        if (!isActive) {
          return null;
        }

        const trimmedValue = value.trim();
        if (!trimmedValue) {
          return null;
        }

        if (!isValidUrl(trimmedValue)) {
          return 'URL is invalid';
        }

        return null;
      },
    },
    validateInputOnChange: true,
    validateInputOnBlur: true,
  });

  useEffect(() => {
    if (opened && caminoData) {
      setIsActive(caminoData.isActive);
      form.setValues({
        parishId: caminoData.parishId?.toString() || '',
        dioceseId: caminoData.dioceseId?.toString() || '',
        url: caminoData.url || '',
        comment: caminoData.comment || '',
      });
    }
  }, [opened, caminoData]);

  const handleSave = async () => {
    if (!caminoData) return;

    const validationResult = form.validate();
    if (validationResult.hasErrors) {
      return;
    }

    setIsSaving(true);
    setSaveError(null);
    try {
      const parishIdValue = parseInt(form.values.parishId, 10);
      const dioceseIdValue = parseInt(form.values.dioceseId, 10);
      const trimmedUrl = form.values.url.trim();
      const trimmedComment = form.values.comment.trim();
      const updatedData: CaminoView = {
        ...caminoData,
        isActive,
        parishId: isActive && !Number.isNaN(parishIdValue) ? parishIdValue : null,
        dioceseId: isActive && !Number.isNaN(dioceseIdValue) ? dioceseIdValue : null,
        url: isActive ? (trimmedUrl ? trimmedUrl : null) : null,
        comment: isActive ? (trimmedComment ? trimmedComment : null) : null,
      };

      await onSave(updatedData);
      if (!isActive) {
        form.setValues({
          parishId: '',
          dioceseId: '',
          url: '',
          comment: '',
        });
      }
    } catch (error) {
      console.error('Failed to save camino data:', error);
      setSaveError(error as AxiosError<ErrorResponse> | null);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Camino Information"
      classNames={{
        header: styles.modalHeader,
        title: styles.modalTitle,
        close: styles.closeButton,
      }}
      size="lg"
    >
      <Stack gap="md" pt="md">
        <Stack gap="xs">
          <Switch
            label="Camino Active"
            labelPosition="left"
            checked={isActive}
            onChange={(e) => setIsActive(e.currentTarget.checked)}
          />
        </Stack>

        {isActive && (
          <Stack gap="md">
            <TextInput
              label="Parish ID"
              placeholder="Enter parish ID"
              required
              type="number"
              {...form.getInputProps('parishId')}
            />
            <TextInput
              label="Diocese ID"
              placeholder="Enter diocese ID"
              required
              type="number"
              {...form.getInputProps('dioceseId')}
            />
            <TextInput label="URL" placeholder="Enter URL" type="url" {...form.getInputProps('url')} />
            <TextInput label="Comment" placeholder="Enter comment" {...form.getInputProps('comment')} />
          </Stack>
        )}

        <Divider my="xs" />
        <SaveErrorBox error={saveError} />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            loading={isSaving}
            color="primary"
            disabled={isActive && (!form.values.parishId.trim() || !form.values.dioceseId.trim())}
          >
            Save
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
